import React, {
  type ReactNode,
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { LogCategory } from '../config/logging';
import { logger, sendToTerminal, sendToTestLog } from '../lib/terminalLogger';
import { normalizePedido } from '../services/orders/orderNormalizer';
import { OrderQueryService } from '../services/orders/orderQueryService';
import { mergePedidosIncremental } from '../services/orders/orderReconciler';
import type { Item, OrderStatus, Pedido } from '../types/orders';
import { getTenantIdForWebSocket } from '../utils/getTenantId';
import { usePermissions } from './PermissionContext';

export type { OrderStatus, Pedido } from '../types/orders';

interface OrderContextData {
  pedidos: Pedido[];
  selectedPedido: Pedido | null;
  loading: boolean;
  error: string | null;
  regrasProducao: unknown[];
  fetchPedidos: (forceRefresh?: boolean) => Promise<Pedido[]>;
  selectPedido: (pedido: Pedido) => void;
  clearSelectedPedido: () => void;
  getOrderStatus: (pedido: Pedido) => OrderStatus;
  updatePedidoInList: (pedidoAtualizado: Pedido) => void;
  markItemArquivoSaved: (orcId: number, oitId: number) => void;
}

interface OrderProviderProps {
  children: ReactNode;
}

const OrderContext = createContext<OrderContextData | undefined>(undefined);

function resolveOrderStatus(pedido: Pedido): OrderStatus {
  if (!pedido?.status || !Array.isArray(pedido.itens)) {
    logger.warn('Pedido malformado em getOrderStatus:', pedido);
    return 'AGUARDANDO';
  }

  switch (pedido.status.id_pagamento) {
    case 1:
      return 'AGUARDANDO';
    case 2:
      return 'PAGO';
    case 3:
      return 'CANCELADO';
    case 4:
      return 'OUTRO';
    case 5:
      return 'PARCIAL';
    default:
      break;
  }

  const firstItem = pedido.itens[0];
  if (!firstItem) return 'AGUARDANDO';

  switch (firstItem.status?.descricao) {
    case 'Em Separação':
      return 'SEPARAÇÃO';
    case 'Em Produção':
      return 'IMPRESSÃO';
    case 'Orçamento':
      return 'AGUARDANDO';
    default:
      break;
  }

  switch (firstItem.estagio) {
    case 'Arte Final':
      return 'ARTE FINAL';
    case 'Impressão':
      return 'IMPRESSÃO';
    case 'Prensagem':
      return 'PRENSAGEM';
    case 'Controle de Qualidade':
      return 'CONTROLE DE QUALIDADE';
    case 'Expedição':
      return 'EXPEDIÇÃO';
    case 'Disponível para Coleta':
      return 'DISPONIVEL PARA COLETA';
    default:
      return 'AGUARDANDO';
  }
}

export function OrderProvider({ children }: OrderProviderProps) {
  const [pedidos, setPedidos] = useState<Pedido[]>([]);
  const [selectedPedido, setSelectedPedido] = useState<Pedido | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [regrasProducao] = useState<unknown[]>([]);
  const hasLoadedOnceRef = useRef(false);
  const requestGenerationRef = useRef(0);
  const { role, userInfo } = usePermissions();
  const tenantKey = getTenantIdForWebSocket();
  const scopeKey = `${tenantKey}:${role ?? 'unresolved'}:${userInfo?.id ?? 'anonymous'}`;

  const orderQueryService = useMemo(() => {
    if (!role) return null;

    return new OrderQueryService({
      scope: {
        tenantKey,
        role,
        userId: userInfo?.id,
      },
      normalizeOrders: (orders, queryRole) =>
        orders.map((order) => normalizePedido(order, { role: queryRole })),
    });
  }, [role, tenantKey, userInfo?.id]);

  useEffect(() => {
    requestGenerationRef.current += 1;
    hasLoadedOnceRef.current = false;
    setPedidos([]);
    setSelectedPedido(null);
    setError(null);
    setLoading(false);
  }, [scopeKey]);

  const fetchPedidos = useCallback(
    async (forceRefresh = false): Promise<Pedido[]> => {
      if (!orderQueryService) {
        const unresolvedError = new Error(
          'Perfil do usuário ainda não foi resolvido',
        );
        setError('Erro ao carregar pedidos. Por favor, tente novamente.');
        throw unresolvedError;
      }

      const generation = requestGenerationRef.current + 1;
      requestGenerationRef.current = generation;

      if (!hasLoadedOnceRef.current) setLoading(true);
      setError(null);

      try {
        const loadedOrders = await orderQueryService.fetchOrders({
          cacheMode: forceRefresh ? 'bypass' : 'validate',
        });

        if (generation !== requestGenerationRef.current) {
          return loadedOrders;
        }

        if (loadedOrders.length === 0 || forceRefresh) {
          setPedidos(loadedOrders);
        } else {
          setPedidos((currentOrders) =>
            mergePedidosIncremental(loadedOrders, currentOrders),
          );
        }

        hasLoadedOnceRef.current = true;
        return loadedOrders;
      } catch (fetchError) {
        if (generation === requestGenerationRef.current) {
          logger.error('Erro ao buscar pedidos:', fetchError);
          setError('Erro ao carregar pedidos. Por favor, tente novamente.');
        }
        throw fetchError;
      } finally {
        if (generation === requestGenerationRef.current) {
          setLoading(false);
        }
      }
    },
    [orderQueryService],
  );

  const selectPedido = useCallback((pedido: Pedido) => {
    setSelectedPedido(pedido);

    sendToTerminal(
      '[ORDER_CONTEXT] Pedido selecionado',
      {
        pedidoId: pedido.orc_id,
        itensLength: pedido.itens?.length,
      },
      LogCategory.EVENT,
    );
    sendToTestLog(
      '[ORDER_CONTEXT] Pedido selecionado',
      {
        pedidoId: pedido.orc_id,
        itensLength: pedido.itens?.length,
      },
      'csharp',
      LogCategory.EVENT,
    );

    try {
      localStorage.setItem('currentPedido', JSON.stringify(pedido));
    } catch (storageError) {
      logger.warn('Erro ao salvar pedido selecionado', storageError);
    }
  }, []);

  const clearSelectedPedido = useCallback(() => {
    setSelectedPedido(null);
  }, []);

  const updatePedidoInList = useCallback((pedidoAtualizado: Pedido) => {
    setPedidos((currentOrders) => {
      const orderIndex = currentOrders.findIndex(
        (order) => order.orc_id === pedidoAtualizado.orc_id,
      );
      if (orderIndex === -1) {
        return [...currentOrders, pedidoAtualizado].sort(
          (first, second) => second.orc_id - first.orc_id,
        );
      }

      const updatedOrders = [...currentOrders];
      const [mergedOrder] = mergePedidosIncremental(
        [pedidoAtualizado],
        [currentOrders[orderIndex]],
      );
      updatedOrders[orderIndex] = mergedOrder;
      return updatedOrders;
    });

    setSelectedPedido((currentSelection) => {
      if (currentSelection?.orc_id !== pedidoAtualizado.orc_id) {
        return currentSelection;
      }
      return mergePedidosIncremental(
        [pedidoAtualizado],
        [currentSelection],
      )[0];
    });
  }, []);

  const markItemArquivoSaved = useCallback((orcId: number, oitId: number) => {
    setPedidos((currentOrders) => {
      const orderIndex = currentOrders.findIndex(
        (order) => order.orc_id === orcId,
      );
      if (orderIndex === -1) return currentOrders;

      const order = currentOrders[orderIndex];
      const savedItem = order.itens.find(
        (item) => String(item.oit_id) === String(oitId),
      );
      if (!savedItem) return currentOrders;

      const getProductId = (item: Item): number | undefined =>
        item.produto?.pro_id;
      const savedProductId = getProductId(savedItem);
      const savedOperationalProductId =
        savedItem.linked_context?.operational_product?.pro_id;

      const isLinkedCounterpart = (item: Item): boolean => {
        if (savedItem.is_impressao_vinculada === true) {
          return (
            item.is_impressao_vinculada !== true &&
            item.linked_context?.operational_product?.pro_id != null &&
            item.linked_context.operational_product.pro_id === savedProductId
          );
        }

        return (
          item.is_impressao_vinculada === true &&
          savedOperationalProductId != null &&
          getProductId(item) === savedOperationalProductId
        );
      };

      let changed = false;
      const items = order.itens.map((item) => {
        const shouldMark =
          String(item.oit_id) === String(oitId) || isLinkedCounterpart(item);
        if (!shouldMark || item.arquivo?.sys_save === true) return item;

        changed = true;
        return {
          ...item,
          arquivo: {
            ...(item.arquivo ?? {
              id: 0,
              nome: null,
              diretorio: null,
            }),
            sys_save: true,
          },
        };
      });

      if (!changed) return currentOrders;

      const updatedOrders = [...currentOrders];
      updatedOrders[orderIndex] = { ...order, itens: items };
      return updatedOrders;
    });
  }, []);

  const getOrderStatus = useCallback(resolveOrderStatus, []);

  const contextValue = useMemo<OrderContextData>(
    () => ({
      pedidos,
      selectedPedido,
      loading,
      error,
      regrasProducao,
      fetchPedidos,
      selectPedido,
      clearSelectedPedido,
      getOrderStatus,
      updatePedidoInList,
      markItemArquivoSaved,
    }),
    [
      pedidos,
      selectedPedido,
      loading,
      error,
      regrasProducao,
      fetchPedidos,
      selectPedido,
      clearSelectedPedido,
      getOrderStatus,
      updatePedidoInList,
      markItemArquivoSaved,
    ],
  );

  return (
    <OrderContext.Provider value={contextValue}>
      {children}
    </OrderContext.Provider>
  );
}

export function useOrders(): OrderContextData {
  const context = useContext(OrderContext);
  if (!context) {
    throw new Error('useOrders must be used within an OrderProvider');
  }
  return context;
}
